// Vendor
import React from "react";
import { Trans } from "@coworker/locales";
// Enums
import { splitLocation } from "../../../services/locations.service";
import {
  DATE_STAMP_WIDGET,
  SUPPLIER_NUMBER_WIDGET,
  PHOTOS_WIDGET,
  PRIORITY_WIDGET,
  DESCRIPTION_WIDGET,
  getProductWidget,
  RADIOBUTTONS_WIDGET,
  SAFETY_ALARM_INFO_WIDGET,
  DESCRIBE_THE_PROBLEM_WIDGET,
  AFFECTED_QUANTITY_WIDGET,
} from "./_common";
import { SECTION, LOCATION } from "../widgets";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";

// Icons
import WrenchIcon from "../../../assets/svg/wrench.svg?react";
import PriorityIcon from "@coworker/reusable/svg/priority-exclamation-sign.svg?react";

import { useWorkspacesAction } from "../../../hooks/useWorkspaces";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useTranslation } from "@coworker/locales";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import SAFETY_ALARM_TITLE from "../common/safety_alarm_title";
import { useProductQualityTeamId } from "@coworker/app/src/hooks/useProductQualityTeamId";

function validate(form) {
  const checks = [
    form.supplier_number,
    form.pq_date_stamp,
    form.description,
    form.location || form.location_custom,
    form.product_article_id,
    form.pq_discovered_type,
    form.affected_quantity,
    !isChineseEnvironment() || form.images?.length, // For .cn we require at least one image to accept a PQ report.
  ];
  return checks.every((x) => x);
}

function fieldsFilledCount(form) {
  let count = 0;
  if (
    form.supplier_number &&
    form.supplier_number !== form.scanned_supplier_number
  )
    count++;
  if (form.pq_date_stamp) count++;
  if (form.description) count++;
  if (form.location || form.location_custom) count++;
  if (form.done_so_far) count++;
  if (form.pq_discovered_type) count++;
  if (form.priority_flag) count++;
  if (form.images && form.images.length > 0) count++; // Do not care if the image is loading. User has interacted with the field if length > 0
  return count;
}

export default {
  table: "tasks",
  type: "product_quality",
  enumType: TaskType.PRODUCT_QUALITY,
  validate,
  fieldsFilledCount,
  header: {
    newTitle: <Trans>capitalCaseProductQualityReportString</Trans>,
    editTitle: <Trans>editTaskString</Trans>,
    testId: "productQualityFromTitle",
  },
  fields: [
    {
      type: SECTION,
      conf: { titleTranslationKey: "productInformationString" },
    },
    getProductWidget(),
    SUPPLIER_NUMBER_WIDGET,
    DATE_STAMP_WIDGET,
    AFFECTED_QUANTITY_WIDGET,
    {
      ...PHOTOS_WIDGET,
      conf: { lblColor: "var(--grey900)" },
    },
    { type: SECTION, conf: { titleTranslationKey: "description4String" } },
    DESCRIBE_THE_PROBLEM_WIDGET,
    {
      type: LOCATION,
      conf: {
        optional: false,
        locationString: <Trans>defectedProductLocationString</Trans>,
        popupName: "pqrLocation",
        deactivated: false,
        newAddonLocation: true,
        noArrow: true,
      },
      values: {
        in: ({
          location,
          location_custom,
          product_article_id,
          product_article_type,
        }) => {
          const [code, department] = splitLocation(
            (location || "").replace(/%26/g, "&").replace(/%2F/g, "/")
          );

          return {
            code,
            department,
            custom: location_custom,
            articleId: product_article_id,
            articleType: product_article_type,
          };
        },
        out: ({ code, department, custom, section }) => {
          let locationText = code || "";
          if (department || section)
            locationText += ` - ${department || section}`;

          let departmentText = "";
          if (department || section)
            departmentText = `${department || section}`;

          return {
            location: custom ? "" : locationText,
            location_grid_code: custom ? "" : code,
            location_department: custom ? "" : departmentText,
            location_custom: custom ? custom : "",
          };
        },
      },
    },
    RADIOBUTTONS_WIDGET,
    DESCRIPTION_WIDGET(
      true,
      "done_so_far",
      "whatOtherActions",
      // TODO: Replace below with <Icon family="icons" name="wrench" size=... />
      <WrenchIcon />,
      "var(--grey900)"
    ),
    {
      ...PRIORITY_WIDGET,
      conf: {
        title: SAFETY_ALARM_TITLE,
        icon: <PriorityIcon />,
        testId: "safetyAlarmCheckbox",
      },
    },
    SAFETY_ALARM_INFO_WIDGET,
  ],
  useReactToChanges: function (taskId, task, onChange) {
    const qualityTeamId = useProductQualityTeamId();
    const { pop } = useWorkspacesAction();
    const { showToast } = useToastNotification();
    const { t } = useTranslation();
    React.useEffect(() => {
      if (!qualityTeamId) {
        try {
          pop();
          showToast(t("noProductQualityTeamString"));
        } catch (error) {
          console.error("Error handling quality team logic:", error);
        }
        return;
      }

      try {
        onChange({ assigned_team_id: qualityTeamId });
      } catch (error) {
        console.error("Error updating team id:", error);
      }
      // Ignored: onChange since it changes on every render :(
      // eslint-disable-next-line
    }, []);
  },
};
